import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { Text } from "../../components/Text";
import { ChatMessageTimestamp } from "../ChatMessageTimestamp";
import type { FileChatMessage, ReplyContext } from "../types";
import { ChatFileAttachment } from "./ChatFileAttachment";
import { ReplyIconButton } from "./ReplyIconButton";

interface ChatFileMessageProps {
  message: FileChatMessage;
  isIncoming: boolean;
  onReply?: (context: ReplyContext) => void;
  sx?: SxProps<Theme>;
}

function formatTimestamp(createdAt: number): string {
  return new Date(createdAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getMessageBubbleStyles(theme: Theme, isIncoming: boolean) {
  return {
    display: "inline-flex",
    flexDirection: "column" as const,
    gap: 1,
    paddingX: 4,
    paddingY: 3,
    borderRadius: theme.borderRadius?.xLarge,
    backgroundColor: isIncoming
      ? (theme.palette.background.chat?.incoming ?? theme.palette.background.muted)
      : (theme.palette.background.chat?.outgoing ?? theme.palette.primary.main),
    maxWidth: "80%",
    overflowWrap: "break-word" as const,
    wordBreak: "break-word" as const,
  };
}

/**
 * Renders a file attachment message with optional caption
 */
export function ChatFileMessage(props: ChatFileMessageProps) {
  const { message, isIncoming, onReply, sx } = props;

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: isIncoming ? "flex-start" : "flex-end",
          gap: 1,
        },
        sx
      )}
    >
      <ChatMessageTimestamp
        senderName={message.senderName ?? "Unknown"}
        timestamp={formatTimestamp(message.createdAt)}
      />
      <Box sx={(theme: Theme) => getMessageBubbleStyles(theme, isIncoming)}>
        <ChatFileAttachment
          fileName={message.fileName}
          fileSize={message.fileSize}
          fileType={message.fileType}
          fileUrl={message.fileUrl}
          thumbnailUrl={message.thumbnailUrl}
        />
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          {message.message && (
            <Text
              variant="body2"
              sx={(theme) => ({
                color: isIncoming
                  ? theme.palette.base?.strong
                  : theme.palette.base?.inverted?.strong,
              })}
            >
              {message.message}
            </Text>
          )}
          {onReply && (
            <ReplyIconButton
              isIncoming={isIncoming}
              sx={{ marginLeft: message.message ? 0 : "auto" }}
              onClick={() => {
                onReply({
                  messageId: message.messageId,
                  message: message.message ?? message.fileName,
                  senderName: message.senderName ?? "Unknown",
                });
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
